import React, { useState, useEffect } from 'react';
import { X, User, Mail, Key, Calendar, Shield, CheckCircle, AlertCircle, UserX, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MemberEditModal = ({ member, isOpen, memberFilter, onClose, onSuccess }) => {
  const { user, impersonationContext } = useAuth();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    birth_number: '',
    role: 'member',
    internal_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [sendingResetLink, setSendingResetLink] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [softDeleting, setSoftDeleting] = useState(false);

  // Only admins and skyteledere (org_admin) see the internal note field
  const canSeeInternalNotes = ['admin', 'org_admin', 'super_admin'].includes(user?.role)
    || impersonationContext?.originalRole === 'super_admin';

  // Load member data when modal opens
  useEffect(() => {
    if (isOpen && member) {
      setFormData({
        full_name: member.full_name || '',
        email: member.email || '',
        birth_number: member.birth_number || '',
        role: member.role || 'member',
        internal_notes: member.internal_notes || ''
      });
      setError('');
      setSuccess('');
    }
  }, [isOpen, member]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format birth number as DDMMYYYY
    if (name === 'birth_number') {
      // Remove any non-digit characters
      let cleanValue = value.replace(/\D/g, '');
      
      // Limit to 8 digits
      if (cleanValue.length > 8) {
        cleanValue = cleanValue.substring(0, 8);
      }
      
      setFormData(prev => ({ ...prev, [name]: cleanValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) {
      setError('Navn er påkrevd');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('E-post er påkrevd');
      return false;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Ugyldig e-postadresse');
      return false;
    }
    
    // Birth number validation (DDMMYYYY format) — only enforce when the user actively
    // changed it. Legacy 6/10/11-digit values from before this validation should not block
    // saving unrelated fields like internal_notes.
    const originalBirth = member?.birth_number || '';
    const birthChanged = (formData.birth_number || '') !== originalBirth;
    if (birthChanged && formData.birth_number && formData.birth_number.length !== 8) {
      setError('Fødselsdato må være 8 siffer (DDMMYYYY)');
      return false;
    }
    
    return true;
  };

  const sendPasswordResetLink = async () => {
    setSendingResetLink(true);
    setError('');
    
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API_BASE}/api/admin/send-password-reset`, {
        user_id: member.id,
        email: member.email
      }, { headers });
      
      setSuccess(`Passord-reset lenke sendt til ${member.email}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to send reset link:', error);
      setError('Kunne ikke sende passord-reset lenke. Prøv igjen.');
    } finally {
      setSendingResetLink(false);
    }
  };

  const deactivateUser = async () => {
    if (window.confirm(`Er du sikker på at du vil deaktivere "${member.full_name}"?\n\nBrukeren vil ikke lenger kunne logge inn, men alle data og treningslogger bevares.`)) {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        // Deaktiverer: setter active=false (ikke approved=false) for å skille klart fra "Venter"-status
        await axios.put(`${API_BASE}/api/users/${member.id}`, { active: false }, { headers });
        setSuccess(`Brukeren "${member.full_name}" ble deaktivert.`);
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose();
        }, 1500);
      } catch (error) {
        console.error('Failed to deactivate user:', error);
        setError(error?.response?.data?.detail || error?.message || 'Kunne ikke deaktivere brukeren. Prøv igjen.');
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteUser = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.delete(`${API_BASE}/api/users/${member.id}`, { headers });
      setSuccess(`Brukeren "${member.full_name}" ble permanent slettet.`);
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose();
        setShowDeleteConfirmation(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError(error?.response?.data?.detail || error?.message || 'Kunne ikke slette brukeren. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  // Soft-delete: markér medlem som Slettet (is_deleted=True). Data bevares, usynlig for admin.
  const softDeleteMember = async () => {
    const confirmed = window.confirm(
      `🗑️ SOFT-SLETT MEDLEM\n\n` +
      `Er du sikker på at du vil slette "${member.full_name}"?\n\n` +
      `• Medlemmet forsvinner fra alle Admin- og Skyteleder-visninger\n` +
      `• Alle data (treningsøkter, våpenkort, merker) bevares i databasen\n` +
      `• Kun SuperAdmin kan gjenopprette medlemmet\n` +
      `• Hvis medlemmet senere registrerer seg i en annen klubb med samme e-post,\n` +
      `  får de tilbud om å reaktivere kontoen med historikken sin\n\n` +
      `Fortsett?`
    );
    if (!confirmed) return;
    setSoftDeleting(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API_BASE}/api/admin/members/${member.id}/soft-delete`, {}, { headers });
      setSuccess(`Medlemmet "${member.full_name}" er slettet. Data er bevart.`);
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to soft-delete member:', error);
      setError(error?.response?.data?.detail || error?.message || 'Kunne ikke slette medlemmet. Prøv igjen.');
    } finally {
      setSoftDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        birth_number: formData.birth_number,
        role: formData.role
      };
      if (canSeeInternalNotes) {
        updateData.internal_notes = formData.internal_notes || '';
      }

      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.put(`${API_BASE}/api/users/${member.id}`, updateData, { headers });

      setSuccess('Medlem oppdatert!');
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose();
        setSuccess('');
      }, 1500);

    } catch (error) {
      console.error('Update failed:', error);
      setError(error?.response?.data?.detail || error?.message || 'Kunne ikke oppdatere medlem');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Rediger Medlem</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Navn
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              E-post
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>

          {/* Birth Number */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Fødselsdato (DDMMYYYY)
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="birth_number"
                value={formData.birth_number}
                onChange={handleInputChange}
                placeholder="15031990"
                maxLength={8}
                className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Rolle
            </label>
            <div className="relative">
              <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="member">Medlem</option>
                <option value="org_admin">Skyteleder</option>
                {/* Admin option visible to super_admin, admin, or super_admin when impersonating */}
                {(user?.role === 'super_admin' || user?.role === 'admin' || impersonationContext?.originalRole === 'super_admin') && (
                  <option value="admin">Admin</option>
                )}
              </select>
            </div>
          </div>

          {/* Password Reset Section */}
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-300 mb-1 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Passord-tilbakestilling
                </h3>
                <p className="text-xs text-gray-400 mb-3">
                  Send en sikker lenke til {member.email} for å tilbakestille passordet.
                  Medlemmet setter sitt eget nye passord.
                </p>
                <button
                  type="button"
                  onClick={sendPasswordResetLink}
                  disabled={sendingResetLink}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {sendingResetLink ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Sender...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Send passord-reset lenke
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-600/20 border border-red-600/50 rounded-md p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-600/20 border border-green-600/50 rounded-md p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                <p className="text-green-400 text-sm">{success}</p>
              </div>
            </div>
          )}

          {/* Admin Actions - KUN admin og super_admin (ikke org_admin/skyteledere) */}
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <div className="border-t border-gray-600 pt-4 mt-6">
              <p className="text-sm text-gray-400 mb-3">Administratorhandlinger:</p>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={deactivateUser}
                  disabled={loading}
                  className="flex items-center space-x-2 py-2 px-4 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-500 transition-colors disabled:opacity-50"
                >
                  <UserX className="h-4 w-4" />
                  <span>Deaktiver</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={loading}
                  className="flex items-center space-x-2 py-2 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-500 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Slett</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                <strong>Deaktiver:</strong> Bruker kan ikke logge inn, men data bevares. <strong>Slett:</strong> Permanent fjerning (ikke reverserbart).
              </p>
            </div>
          )}

          {/* Soft-slett medlem: kun synlig når Admin/SuperAdmin filtrerer på "Deaktiverte" (ikke skyteledere) */}
          {memberFilter === 'deactivated' && (user?.role === 'admin' || user?.role === 'super_admin') && (
            <div className="border-t-2 border-gray-500 pt-5 mt-6 bg-gray-900/40 -mx-6 px-6 pb-4 rounded-b-lg">
              <p className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-gray-400" />
                Fjern permanent fra listen
              </p>
              <p className="text-xs text-gray-400 mb-3">
                Soft-sletter medlemmet. Data bevares, men medlemmet forsvinner fra alle Admin- og Skyteleder-visninger. Kun SuperAdmin kan gjenopprette.
              </p>
              <button
                type="button"
                onClick={softDeleteMember}
                disabled={softDeleting || loading}
                className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-gray-700 border-2 border-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 hover:border-gray-400 transition-colors disabled:opacity-50"
                data-testid="soft-delete-member-btn"
              >
                <Trash2 className="h-4 w-4" />
                <span>{softDeleting ? 'Sletter…' : 'Slett medlem'}</span>
              </button>
            </div>
          )}

          {/* Internt notat – kun synlig for admin/skyteledere/super_admin */}
          {canSeeInternalNotes && (
            <div className="pt-2 border-t border-gray-700">
              <label className="block text-sm font-medium text-yellow-300 mb-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Internt notat (kun synlig for Skyteledere / Admin)
              </label>
              <textarea
                name="internal_notes"
                value={formData.internal_notes}
                onChange={handleInputChange}
                rows={4}
                maxLength={5000}
                placeholder="Notater her er synlige for klubbens skyteledere og admin, men ikke for medlemmet selv."
                className="w-full px-4 py-2 bg-gray-900/60 border border-yellow-700/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-y"
                data-testid="member-edit-internal-notes"
              />
              {member?.internal_notes_updated_by && (
                <p className="text-xs text-gray-400 mt-1">
                  Sist redigert av {member.internal_notes_updated_by}
                  {member.internal_notes_updated_at && (
                    <> – {new Date(member.internal_notes_updated_at).toLocaleString('nb-NO')}</>
                  )}
                </p>
              )}
              <div className="text-[10px] text-gray-500 mt-1 text-right">{(formData.internal_notes || '').length}/5000</div>
            </div>
          )}

          {/* Main Buttons */}
          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-colors"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center py-3 px-4 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
            >
              {loading ? 'Lagrer...' : 'Lagre Endringer'}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={deleteUser}
        userName={member?.full_name || ''}
        loading={loading}
      />
    </div>
  );
};

export default MemberEditModal;